"use client"

import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  fetchStudents,
  fetchStudentById,
  updateAdmissionStatus,
  deleteStudent,
  clearError,
  setHodVerification,
  setAccountVerification,
} from "../store/slices/studentSlice"
import AddStudent from "./AddStudent"
import EditStudent from "./EditStudent"
import ViewStudent from "./ViewStudent"
import StudentIDCard from "./StudentIDCard"
import {
  Users,
  Plus,
  Eye,
  Edit,
  Trash2,
  Search,
  Filter,
  X,
  Loader2,
  CheckCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  Award as IdCard,
} from "lucide-react"

const PRIMARY_COLOR = "blue-600"
const PRIMARY_HOVER = "blue-700"
const ACCENT_COLOR = "green-600"
const DANGER_COLOR = "red-600"

const getStatusBadge = (status) => {
  const normalizedStatus = status ? status.toLowerCase() : "pending review"
  switch (normalizedStatus) {
    case "enrolled":
      return {
        text: "Enrolled",
        bg: "bg-green-100",
        textCol: "text-green-800",
        icon: <CheckCircle className="w-3 h-3 mr-1" />,
      }
    case "rejected":
      return {
        text: "Rejected",
        bg: "bg-red-100",
        textCol: "text-red-800",
        icon: <X className="w-3 h-3 mr-1" />,
      }
    default:
      return {
        text: "Pending Review",
        bg: "bg-yellow-100",
        textCol: "text-yellow-800",
        icon: <Clock className="w-3 h-3 mr-1" />,
      }
  }
}

const StudentsComponent = ({ facultyMode = false, departmentId = null }) => {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const { students, currentStudent, loading, error } = useSelector((state) => state.students)
  const [editingStudent, setEditingStudent] = useState(null)
  const [viewingStudent, setViewingStudent] = useState(null)
  const [viewingICard, setViewingICard] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("ALL")
  const [message, setMessage] = useState({ text: "", type: "" })
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const handleDelete = (studentId) => {
    dispatch(deleteStudent(studentId))
      .unwrap()
      .then(() => setMessage({ text: "Student deleted successfully!", type: "success" }))
      .catch((err) => {
        const errorMessage = err?.message || err?.error || String(err)
        setMessage({ text: `Error deleting student: ${errorMessage}`, type: "error" })
      })
  }

  const handleAccountVerificationChange = (studentId, value) => {
    const received = String(value).toUpperCase() === "RECEIVED"
    dispatch(setAccountVerification({ studentId, received }))
      .then(() => setMessage({ text: "Accounts verification updated!", type: "success" }))
      .catch((err) => {
        const errorMessage = err?.payload || err?.message || "Unknown error"
        setMessage({ text: `Error updating accounts verification: ${errorMessage}`, type: "error" })
      })
  }

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "super_admin" || user?.role === "account") {
      dispatch(fetchStudents())
    } else if (user?.role === "faculty") {
      const facultyId = user.faculty_id
      if (facultyId) {
        dispatch(fetchStudents({ facultyId }))
      }
    } else if (user?.role === "student") {
      const id = user.student_id || user.id
      if (id) {
        dispatch(fetchStudentById(id))
      }
    }
  }, [dispatch, user])

  useEffect(() => {
    if (error) {
      setMessage({ text: `Error: ${error}`, type: "error" })
      dispatch(clearError())
    }
  }, [error, dispatch])

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ text: "", type: "" }), 3000)
      return () => clearTimeout(timer)
    }
  }, [message])

  const handleStatusChange = (studentId, status) => {
    dispatch(updateAdmissionStatus({ id: studentId, status }))
      .unwrap()
      .then(() => {
        setMessage({ text: "Admission status updated successfully!", type: "success" })
      })
      .catch((err) => {
        const errorMessage = err?.message || err?.error || String(err)
        setMessage({ text: `Error updating status: ${errorMessage}`, type: "error" })
      })
  }

  const handleHodVerificationChange = (studentId, value) => {
    const received = value === "RECEIVED"
    dispatch(setHodVerification({ studentId, received }))
      .unwrap()
      .then(() => setMessage({ text: "HOD verification updated!", type: "success" }))
      .catch((err) => {
        const errorMessage = err?.message || err?.error || String(err)
        setMessage({ text: `Error updating verification: ${errorMessage}`, type: "error" })
      })
  }

  const getFilteredStudents = () => {
    let studentsToFilter = students

    if (facultyMode && departmentId) {
      studentsToFilter = students.filter((student) => student.department_id === departmentId)
    }

    return studentsToFilter.filter((student) => {
      const matchesSearch =
        student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.student_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.mobile_no?.includes(searchTerm) ||
        student.email?.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesStatus =
        statusFilter === "ALL" ||
        (statusFilter === ""
          ? !student.admission_status || student.admission_status === ""
          : student.admission_status === statusFilter)

      return matchesSearch && matchesStatus
    })
  }

  const filteredStudents = getFilteredStudents()

  // Pagination logic
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedStudents = filteredStudents.slice(startIndex, endIndex)

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value))
    setCurrentPage(1) // Reset to first page when items per page changes
  }

  if (loading)
    return (
      <div className={`flex items-center justify-center py-12 text-${PRIMARY_COLOR}`}>
        <Loader2 className="animate-spin h-8 w-8" />
        <span className="ml-2 text-gray-600">Loading student records...</span>
      </div>
    )

  return (
    <div className="p-4 sm:p-0">
      {/* Header and Add Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b pb-4">
        <div className="flex items-center gap-3">
          <Users className={`w-8 h-8 text-${PRIMARY_COLOR}`} />
          <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            {facultyMode ? "Institute Students" : "Student Management"}
          </h2>
        </div>

        {/* Add Student Button (Conditional) */}
        {(user?.role === "admin" ||
          user?.role === "super_admin" ||
          user?.role === "account" ||
          user?.role === "hod" ||
          (user?.role === "faculty" && facultyMode)) && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className={`flex items-center gap-2 bg-${PRIMARY_COLOR} hover:bg-${PRIMARY_HOVER} text-white font-semibold py-2 px-5 rounded-xl shadow-md transition-all transform hover:scale-[1.02]`}
          >
            {showAddForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            {showAddForm ? "Close Form" : "Add New Student"}
          </button>
        )}
      </div>

      {/* Message Alerts */}
      {message.text && (
        <div
          className={`p-4 rounded-xl shadow-md border-l-4 ${
            message.type === "success"
              ? `border-${ACCENT_COLOR} bg-green-50 text-green-800`
              : `border-${DANGER_COLOR} bg-red-50 text-red-800`
          } font-medium`}
        >
          <p>{message.text}</p>
        </div>
      )}

      {/* Search and Filter Bar */}
      {(user?.role === "admin" ||
        user?.role === "super_admin" ||
        user?.role === "account" ||
        user?.role === "hod" ||
        user?.role === "faculty") &&
        !showAddForm && (
          <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 mb-6">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="flex-1 relative w-full">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, ID, email, or mobile number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                />
              </div>

              <div className="flex items-center gap-2 w-full md:w-auto">
                <Filter className="w-5 h-5 text-gray-500 flex-shrink-0" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full md:w-48 px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
                >
                  <option value="ALL">All Status</option>
                  <option value="Enrolled">Enrolled</option>
                  <option value="Rejected">Rejected</option>
                  <option value="">Pending Review</option>
                </select>
              </div>
            </div>
          </div>
        )}

      {/* Add Student Form (Modal-like) */}
      {(user?.role === "admin" ||
        user?.role === "super_admin" ||
        user?.role === "account" ||
        user?.role === "hod" ||
        (user?.role === "faculty" && facultyMode)) &&
        showAddForm && (
          <div className="overflow-x-hidden bg-white p-8 rounded-xl shadow-2xl border border-blue-100 transition-all duration-300">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">Register New Student</h3>
            <AddStudent
              setShowAddForm={setShowAddForm}
              restrictedDepartmentId={facultyMode ? departmentId : null}
              facultyMode={facultyMode}
            />
          </div>
        )}

      {/* Student Profile (Student Role) */}
      {user?.role === "student" && currentStudent && (
        <div className="bg-white p-8 rounded-xl shadow-2xl border-l-4 border-blue-500">
          <ViewStudent student={currentStudent} isProfile={true} />
        </div>
      )}

      {/* Student List Table (Admin/Faculty View) */}
      {(user?.role === "admin" ||
        user?.role === "super_admin" ||
        user?.role === "account" ||
        user?.role === "hod" ||
        user?.role === "faculty") && (
        <div className="space-y-6">
          {paginatedStudents.length === 0 ? (
            <div className="bg-white p-12 rounded-xl shadow-lg text-center border-2 border-dashed border-gray-300">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-xl font-semibold text-gray-600">No Students Found</p>
              <p className="text-gray-500 mt-1">Try adjusting your search or filter criteria.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                        id
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                        Name
                      </th>

                   
                      {user?.role === "faculty" ? (
  <>
  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                        Trade
                      </th> 
                      </>
) : <>
    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
      HOD Verification
    </th>
    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
      Accounts Verification
    </th>
  </>}
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                        Fees
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {paginatedStudents.map((student) => {
                      const badge = getStatusBadge(student.admission_status)
                      const paidAmt = Number(student?.total_paid_amount ?? 0)
                      const dueAmt = Number(student?.total_pending_amount ?? 0)
                      const hasAnyFees = paidAmt > 0 || dueAmt > 0
                      return (
                        <tr key={student.student_id} className="hover:bg-blue-50/50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {student.student_id || "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {student.name}
                          </td>

              


                            {user?.role === "faculty" ? (
 <>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {student.trade || "N/A"}
                          </td> 
 </>
) : <>
    <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {user?.role === "hod" ? (
                              <select
                                value={student.hod_verification_status || "PENDING"}
                                onChange={(e) => handleHodVerificationChange(student.student_id, e.target.value)}
                                className={`text-xs px-2 py-1 border border-gray-300 rounded shadow-sm bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-${PRIMARY_COLOR}`}
                              >
                                <option value="PENDING">Pending</option>
                                <option value="RECEIVED">Amount Received</option>
                                <option value="NOT_RECEIVED">Not Received</option>
                              </select>
                            ) : user?.role === "admin" || user?.role === "super_admin" || user?.role === "account" ? (
                              (() => {
                                const v = (student.hod_verification_status || "PENDING").toUpperCase()
                                const cfg =
                                  v === "RECEIVED"
                                    ? { bg: "bg-green-100", text: "text-green-800", label: "Amount Received" }
                                    : v === "NOT_RECEIVED"
                                      ? { bg: "bg-red-100", text: "text-red-800", label: "Not Received" }
                                      : { bg: "bg-yellow-100", text: "text-yellow-800", label: "Pending" }
                                return (
                                  <span
                                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}
                                  >
                                    {cfg.label}
                                  </span>
                                )
                              })()
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {user?.role === "account" ? (
                              <select
                                value={student.accounts_verification_status || "PENDING"}
                                onChange={(e) => handleAccountVerificationChange(student.student_id, e.target.value)}
                                className={`text-xs px-2 py-1 border border-gray-300 rounded shadow-sm bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-${PRIMARY_COLOR}`}
                              >
                                <option value="PENDING">Pending</option>
                                <option value="RECEIVED">Amount Received</option>
                                <option value="NOT_RECEIVED">Not Received</option>
                              </select>
                            ) : (
                              (() => {
                                const v = (student.accounts_verification_status || "PENDING").toUpperCase()
                                const cfg =
                                  v === "RECEIVED"
                                    ? { bg: "bg-green-100", text: "text-green-800", label: "Amount Received" }
                                    : v === "NOT_RECEIVED"
                                      ? { bg: "bg-red-100", text: "text-red-800", label: "Not Received" }
                                      : { bg: "bg-yellow-100", text: "text-yellow-800", label: "Pending" }
                                return (
                                  <span
                                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}
                                  >
                                    {cfg.label}
                                  </span>
                                )
                              })()
                            )}
                          </td>
  </>}
                          
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {user?.role === "admin" || user?.role === "super_admin" ? (
                              <select
                                value={student.admission_status || ""}
                                onChange={(e) => handleStatusChange(student.student_id, e.target.value)}
                                className={`text-xs px-2 py-1 border border-gray-300 rounded shadow-sm bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-${PRIMARY_COLOR}`}
                              >
                                <option value="">Pending Review</option>
                                <option value="Enrolled">Enrolled</option>
                                <option value="Rejected">Rejected</option>
                              </select>
                            ) : (
                              <div
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.textCol}`}
                              >
                                {badge.icon}
                                {badge.text}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {hasAnyFees ? (
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                  Paid ₹{paidAmt.toLocaleString("en-IN")}
                                </span>
                                {dueAmt > 0 ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                                    Due ₹{dueAmt.toLocaleString("en-IN")}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                    No Dues
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => setViewingStudent(student)}
                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-full transition-colors group"
                                title="View Details"
                              >
                                <Eye className="w-5 h-5 group-hover:scale-110 transition-transform" />
                              </button>
                              <button
                                onClick={() => setViewingICard(student)}
                                className="p-2 text-orange-600 hover:bg-orange-100 rounded-full transition-colors group"
                                title="View ID Card"
                              >
                                <IdCard className="w-5 h-5 group-hover:scale-110 transition-transform" />
                              </button>
                              {(user?.role === "admin" ||
                                user?.role === "super_admin" ||
                                user?.role === "account" ||
                                user?.role === "hod") && (
                                <button
                                  onClick={() => setEditingStudent(student)}
                                  className="p-2 text-green-600 hover:bg-green-100 rounded-full transition-colors group"
                                  title="Edit Student"
                                >
                                  <Edit className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                </button>
                              )}
                              {(user?.role === "admin" || user?.role === "super_admin" || user?.role === "account") && (
                                <button
                                  onClick={() => handleDelete(student.student_id)}
                                  className="p-2 text-red-600 hover:bg-red-100 rounded-full transition-colors group"
                                  title="Delete Student"
                                >
                                  <Trash2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {/* Pagination Controls */}
              <div className="flex flex-col sm:flex-row justify-between items-center p-4 border-t border-gray-200">
                <div className="flex items-center space-x-2 mb-4 sm:mb-0">
                  <span className="text-sm text-gray-600">Show</span>
                  <select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="border border-gray-300 rounded-lg p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-sm text-gray-600">per page</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg ${
                      currentPage === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : `bg-${PRIMARY_COLOR} text-white hover:bg-${PRIMARY_HOVER}`
                    }`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="flex space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1 rounded-lg text-sm ${
                          currentPage === page
                            ? `bg-${PRIMARY_COLOR} text-white`
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg ${
                      currentPage === totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : `bg-${PRIMARY_COLOR} text-white hover:bg-${PRIMARY_HOVER}`
                    }`}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Modal Overlay */}
      {editingStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center p-4 z-50 transition-opacity duration-300">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto transform scale-100 transition-transform duration-300">
            <div className="p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">Edit Student Profile</h3>
              <EditStudent student={editingStudent} setEditingStudent={setEditingStudent} />
            </div>
          </div>
        </div>
      )}

      {/* View Modal Overlay */}
      {viewingStudent && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1300]">
          <ViewStudent student={viewingStudent} setViewingStudent={setViewingStudent} />
        </div>
      )}

      {viewingICard && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1400]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-6">
            <StudentIDCard student={viewingICard} onClose={() => setViewingICard(null)} />
          </div>
        </div>
      )}
    </div>
  )
}

export default StudentsComponent
